import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, CreditCard, AlertTriangle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SubscriptionCard } from "@/components/account/SubscriptionCard";
import { PauseDialog, ChangePlanDialog, CancelDialog } from "@/components/account/ActionDialogs";
import { AddressDialog } from "@/components/account/AddressDialog";
import { AddressCard, SupportCard, Card } from "@/components/account/SideCards";
import { PaymentsTable } from "@/components/account/HistoryTables";
import { SettingsPanel } from "@/components/account/SettingsPanel";
import { DeliveryList } from "@/components/DeliveryList";
import { http, authCfg, errorMessage, usePlans } from "@/lib/api";

const toSub = (s) => ({
  plan: s.plan,
  pendingPlan: s.pending_plan,
  nextBilling: new Date(s.next_billing_date || Date.now()),
  nextDelivery: s.next_delivery_date ? new Date(`${s.next_delivery_date}T12:00:00`) : null,
  pause: s.status === "paused" && s.pause_until ? { until: new Date(s.pause_until) } : null,
  cancelsOn: s.cancel_at_period_end ? new Date(s.next_billing_date || Date.now()) : null,
  pastDue: s.payment_failed,
});

const BillingInfo = ({ failed, sub }) => {
  const [busy, setBusy] = useState(false);
  const teyaSub = sub?.provider === "teya";
  const updateCard = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/account/update-card", { origin_url: window.location.origin }, authCfg());
      window.location.assign(data.url);
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(false);
    }
  };
  return (
  <Card title="Billing" icon={CreditCard} testid="billing-card">
    {failed && (
      <p className="mb-3 flex gap-2 rounded-xl bg-red-500/10 p-3 text-sm text-red-200" data-testid="billing-failed">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Your last payment didn't go through. {teyaSub ? "Please update your card below and we'll try again automatically." : "Please check your email for a payment link, or contact us."}
      </p>
    )}
    {sub?.card_last4 && <p className="mb-2 font-semibold" data-testid="billing-card-saved">{sub.card_brand || "Card"} •••• {sub.card_last4}</p>}
    <p className="text-sm text-[#A1A1AA]">Payments are taken securely by our payment provider{teyaSub ? ", Teya" : ""}. We never see or store your card details.{teyaSub ? "" : " To change your card, just get in touch."}</p>
    {teyaSub && sub.status !== "cancelled" && (
      <button onClick={updateCard} disabled={busy} className="lc-btn-ghost mt-4 w-full !py-3" data-testid="billing-update-card">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update card"}
      </button>
    )}
  </Card>
  );
};

const NoSub = ({ ended }) => (
  <section className="rounded-3xl border border-[#262626] bg-[#101010] p-8" data-testid="account-no-subscription">
    <h2 className="font-display text-2xl font-bold">{ended ? "Your subscription has ended" : "No subscription yet"}</h2>
    <p className="mt-2 text-[#A1A1AA]">{ended ? "Thanks for being a WeeLogRun member. You're welcome back any time." : "Choose a plan to start your monthly log deliveries."}</p>
    <Link to="/plans" className="lc-btn-primary mt-6 inline-flex" data-testid="account-rejoin">{ended ? "Rejoin WeeLogRun" : "See plans"}</Link>
  </section>
);

export const MyAccount = ({ onSignOut }) => {
  const [data, setData] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [dialog, setDialog] = useState(null);
  const { data: plans = [] } = usePlans();
  const load = useCallback(async () => {
    try {
      setData((await http.get("/account", authCfg())).data);
    } catch (err) {
      if (err?.response?.status === 401) onSignOut();
      else toast.error(errorMessage(err));
    }
  }, [onSignOut]);
  useEffect(() => {
    load();
    http.get("/account/deliveries", authCfg()).then(({ data: d }) => setDeliveries(d)).catch(() => setDeliveries([]));
  }, [load]);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("card") === "updated") {
      toast.success("Thanks, your new card is being saved.", { description: "It can take a minute to show here." });
      window.history.replaceState(null, "", "/account");
    }
  }, []);

  const call = async (method, path, body) => {
    try {
      const { data: r } = await http[method](`/account/${path}`, body, authCfg());
      await load();
      return r;
    } catch (err) {
      throw new Error(errorMessage(err));
    }
  };
  const act = async (path, body) => {
    try {
      const r = await call("post", path, body);
      setDialog(null);
      toast.success(r.message, { description: "We've emailed you a confirmation." });
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!data) return <div className="grid min-h-screen place-items-center" data-testid="account-loading"><Loader2 className="h-8 w-8 animate-spin text-[#FF6A00]" /></div>;
  const c = data.customer;
  const s = data.subscription;
  const live = s && s.status !== "cancelled";
  const sub = live ? toSub(s) : null;
  const customer = { name: c.full_name || "", email: c.email, phone: c.phone || "", address: c.address || {}, instructions: c.instructions };
  const dlg = (k) => ({ open: dialog === k, onOpenChange: (o) => !o && setDialog(null), sub });
  const payments = data.payments.map((p) => ({ id: p.transaction_id, label: p.transaction_id.startsWith("fail_") ? "Failed attempt" : `#${p.transaction_id.slice(-8).toUpperCase()}`, date: new Date(p.created_at), amount: p.amount_pence, status: p.status === "paid" ? "Paid" : "Failed", url: p.invoice_url }));

  return (
    <div className="lc-container pt-28 pb-24" data-testid="account-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="lc-eyebrow">My WeeLogRun</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="account-welcome">Welcome back, {customer.name.split(" ")[0] || "there"}.</h1>
        </div>
        <button onClick={onSignOut} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F7F5F0]" data-testid="account-signout"><LogOut className="h-4 w-4" /> Sign out</button>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-8">
          {live ? (
            <SubscriptionCard sub={sub} plan={plans.find((p) => p.slug === s.plan) || data.plan_info} pendingPlan={plans.find((p) => p.slug === s.pending_plan)} open={setDialog} onResume={() => act("resume")} canSkip={false} canChangePlan={!data.plan_info?.custom} />
          ) : <NoSub ended={!!s} />}
          <div className="mt-6 rounded-3xl border border-[#262626] bg-[#101010] p-4 sm:p-6">
            <Tabs defaultValue="deliveries">
              <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-full bg-[#0a0a0a] p-1">
                {[["deliveries", "Your deliveries"], ["payments", "Payments"], ["settings", "Account settings"]].map(([v, l]) => (
                  <TabsTrigger key={v} value={v} className="rounded-full px-4 py-2 data-[state=active]:bg-[#FF6A00] data-[state=active]:text-[#080808]" data-testid={`account-tab-${v}`}>{l}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="deliveries" className="mt-5">
                <DeliveryList rows={deliveries} cfg={authCfg} photoUrl={(id) => `/account/deliveries/${id}/photo`} testid="account-deliveries" />
              </TabsContent>
              <TabsContent value="payments" className="mt-5">
                {payments.length ? <PaymentsTable rows={payments} demo={false} /> : <p className="text-sm text-[#A1A1AA]" data-testid="payments-empty">No payments yet.</p>}
              </TabsContent>
              <TabsContent value="settings" className="mt-5">
                <SettingsPanel customer={customer} showReminders={false} onSave={(f) => call("put", "details", { full_name: f.name, phone: f.phone })} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
        <aside className="min-w-0 space-y-6 lg:col-span-4">
          <AddressCard customer={{ ...customer, address: { line1: "", town: "", postcode: "", ...customer.address } }} onEdit={() => setDialog("address")} />
          <BillingInfo failed={s?.payment_failed} sub={s} />
          <SupportCard />
        </aside>
      </div>
      {live && (
        <>
          <PauseDialog {...dlg("pause")} onConfirm={(months) => act("pause", { months })} />
          {dialog === "plan" && <ChangePlanDialog {...dlg("plan")} plans={plans} onConfirm={(plan) => act("change-plan", { plan })} />}
          <CancelDialog {...dlg("cancel")} onConfirm={() => act("cancel")} />
        </>
      )}
      {dialog === "address" && (
        <AddressDialog open onOpenChange={(o) => !o && setDialog(null)} customer={customer}
          onSave={({ address, instructions }) => call("put", "address", { ...address, instructions })} />
      )}
    </div>
  );
};
